import * as a from '@mitranim/js/all.mjs'
import * as p from '@mitranim/js/path.mjs'
import * as c from './conf.mjs'
import * as uma from './util-math.mjs'
import * as uti from './util-time.mjs'
import * as ucc from './util-coll-cls.mjs'
import * as um from './util-misc.mjs'

// TODO All dates and times should be displayed as specified in the user locale. This
// should especially apply to time, because there are users who do not understand the
// 24h time format.

export function isPrintable(val) {return a.isScalar(val) && !a.isJunk(val)}
export function reqPrintable(val) {return a.req(val, isPrintable)}

/*
Mimics Prax's stringification rule, but instead of throwing
on "non-stringifiable" objects, encodes them as JSON. This is useful for some
edge cases, such as selectable values that are arbitrary plain data structures,
but must be passed through the DOM, which supports only strings.
*/
export function toStr(val) {return a.renderLax(val) || a.jsonEncode(val)}
export function maybeToString(val) {return a.isNil(val) ? undefined : String(val)}

// TODO use statically initialized `Intl.NumberFormat`.
export function formatNumber(val, decimals) {
  if (a.isNil(val)) return ``
  a.reqNum(val)
  decimals = validDecimalCount(decimals)

  if (!a.isFin(val)) return ``

  return val.toLocaleString(c.FORMAT_LOCALE, {
    maximumFractionDigits: decimals,
    // We disable grouping because it may cause the number to break over
    // multiple lines in very narrow layouts, particularly if the locale uses
    // spaces.
    useGrouping: false,
  })
}

// Warning: for machine-parsable output, use `machineFormatPercentage`. This may
// generate a localized representation.
export function formatPercentage(val, decimals) {
  if (a.isNil(val)) return ``
  a.reqFin(val)
  return a.optSuf(formatNumber(val * 100, decimals), `%`)
}

// Version of `formatPercentage` that should generate machine-parsable numbers.
export function machineFormatPercentage(val, decimals) {
  if (a.isNil(val)) return ``
  a.reqFin(val)
  decimals = validDecimalCount(decimals)
  return `${uma.truncTo(val * 100, decimals)}%`
}

function validDecimalCount(val) {
  return a.optNat(val) ?? c.DEFAULT_DECIMAL_PLACES
}

// TODO move to `util-time.mjs`.
export function formatDateIso(val) {return uti.toValidDate(val).toISOString()}

// TODO move to `util-time.mjs`.
// Redundant with `a.DateShort`, TODO dedup.
export function formatDateIsoWithoutTime(val) {
  const match = formatDateIso(val).match(/(\d\d\d\d-\d\d-\d\d)/)
  return match ? match[1] : ``
}

// TODO move to `util-time.mjs`.
// Redundant with `DateTimeHuman`, TODO dedup.
export function formatDateIsoWithTime(val) {
  const match = formatDateIso(val).match(/(\d\d\d\d-\d\d-\d\d)T(\d\d:\d\d)/)
  return match ? `${match[1]} ${match[2]}` : ``
}

// TODO move to `util-time.mjs`.
export function formatDateIsoWithTimeTimezoneOffset(val) {
  val = uti.toValidDateOpt(val)
  if (!val) return undefined
  val = val.getTime() - (val.getTimezoneOffset() * a.MS_IN_MIN)
  return formatDateIsoWithTime(val)
}

// TODO move to `util-time.mjs`.
export class DateTimeFormat extends Intl.DateTimeFormat {
  /*
  Workaround for an issue in Chrome/V8. When formatting 24-hour time, instead of
  rendering 00:XX, it renders 24:XX. Safari doesn't have this problem.
  */
  format(val) {
    return a.laxStr(super.format(val)).replace(/\b24:/, `00:`)
  }

  formatOpt(val) {
    return a.laxStr((val = uti.toValidDateOpt(val)) && this.format(val))
  }

  /*
  Workaround for a bug / problem in the `Intl.DateTimeFormat` API. Its
  constructor allows only a closed set of "known" timezones, but it doesn't
  ACTUALLY know about all timezones. On some systems, in some browsers, the
  system provides a timezone name that this API doesn't know about. We feed
  the output of this method into other `DateTimeFormat` constructor calls,
  some of them synchronously during our script initialization. The resulting
  exceptions break our script initialization COMPLETELY. This workaround
  should avoid that.
  */
  resolvedOptions() {
    const out = super.resolvedOptions()
    delete out.timeZone
    return out
  }
}

// TODO move to `util-time.mjs`.
export function shortTimezone(val) {
  return fmtShortTimezone.formatOpt(val)?.split(` `)[2]
}

// This locale must be hardcoded.
const fmtShortTimezone = new DateTimeFormat(`en-US`, {timeZoneName: `short`})

// TODO move to `util-time.mjs`.
export function formatDateHuman(val) {
  return fmtDateHuman.formatOpt(val)
}

export const fmtDateHuman = new DateTimeFormat(c.FORMAT_LOCALE, {
  year: `numeric`,
  month: `short`,
  day: `numeric`,
})

// TODO move to `util-time.mjs`.
export function formatDateHumanWithWeekday(val) {
  return fmtDateHumanWithWeekday.formatOpt(val)
}

export const fmtDateHumanWithWeekday = new DateTimeFormat(c.FORMAT_LOCALE, {
  ...fmtDateHuman.resolvedOptions(),
  weekday: `long`,
})

// TODO move to `util-time.mjs`.
export function formatDateHumanWithTime(val) {
  return fmtDateHumanWithTime.formatOpt(val)
}

export const fmtDateHumanWithTime = new DateTimeFormat(c.FORMAT_LOCALE, {
  ...fmtDateHuman.resolvedOptions(),
  hour: `2-digit`,
  minute: `2-digit`,
  hour12: false,
})

// TODO move to `util-time.mjs`.
export function formatDateHumanWithTimeAndTimezone(val) {
  return fmtDateHumanWithTimeAndTimezone.formatOpt(val)
}

export const fmtDateHumanWithTimeAndTimezone = new DateTimeFormat(c.FORMAT_LOCALE, {
  ...fmtDateHumanWithTime.resolvedOptions(),
  timeZoneName: `short`,
})

// TODO move to `util-time.mjs`.
export function formatDateHumanNoYear(val) {
  return fmtDateHumanNoYear.formatOpt(val)
}

export const fmtDateHumanNoYear = new DateTimeFormat(c.FORMAT_LOCALE, {
  month: `long`,
  day: `numeric`,
})

// TODO move to `util-time.mjs`.
export function formatDateHumanNoDay(val) {
  return fmtDateHumanNoDay.formatOpt(val)
}

export const fmtDateHumanNoDay = new DateTimeFormat(c.FORMAT_LOCALE, {
  year: `numeric`,
  month: `long`,
})

class DateTimeFormatCacheForHoursMinutes extends a.Cache {
  make(key) {
    return new DateTimeFormat(key, {hour: `numeric`, minute: `numeric`})
  }
}

export function formatDateAsHoursMinutesBySystemSettings(val, lang) {
  return DateTimeFormatCacheForHoursMinutes
    .main
    .goc(lang)
    .format(uti.toValidDate(val))
}

// TODO move to `util-time.mjs`.
export function formatDateAsHoursMinutes(val, tzm) {
  const date = uti.toValidDate(val)

  const hours = uti.tzmodeIsUtc(tzm) ? date.getUTCHours() : date.getHours()
  const minutes = uti.tzmodeIsUtc(tzm) ? date.getUTCMinutes() : date.getMinutes()

  const hoursStr = padZero2(hours)
  const minutesStr = padZero2(minutes)

  return `${hoursStr}:${minutesStr}`
}

// TODO move to `util-time.mjs`.
export function formatDateAsLocalHoursMinutes(val) {
  return formatDateAsHoursMinutes(val, c.TZMODE_LOCAL)
}

// TODO move to `util-time.mjs`.
export function formatDateAsHoursMinutesSeconds(val, tzm) {
  const date = uti.toValidDate(val)

  const hours   = uti.tzmodeIsUtc(tzm) ? date.getUTCHours()   : date.getHours()
  const minutes = uti.tzmodeIsUtc(tzm) ? date.getUTCMinutes() : date.getMinutes()
  const seconds = uti.tzmodeIsUtc(tzm) ? date.getUTCSeconds() : date.getSeconds()

  return formatTime(hours, minutes, seconds)
}

// TODO move to `util-time.mjs`.
export function formatDateAsLocalHoursMinutesSeconds(val) {
  return formatDateAsHoursMinutesSeconds(val, c.TZMODE_LOCAL)
}

// TODO move to `util-time.mjs`.
export function formatTime(hours, minutes, seconds) {
  return joinColon(
    padZero2(a.laxFin(hours)),
    padZero2(a.laxFin(minutes)),
    a.laxFin(seconds) ? padZero2(seconds) : ``,
  )
}

// TODO move to `util-time.mjs`.
export function dateFormatFun(date, time) {
  return (
    date && time ? formatDateHumanWithTime              :
    date         ? formatDateHuman                      :
    time         ? formatDateAsLocalHoursMinutesSeconds :
    undefined
  )
}

const NUMBER_REGEX = /^[+-]?\d+(?:[.]\d+)?$/

export function isStrNum(val) {return NUMBER_REGEX.test(val)}

/*
Differences from `Number.parseFloat`:

  * Returns either `undefined` or finite number, never NaN or ±Infinity.

  * Accepts nil, number, or string. Rejects other inputs.

  * Requires the entire string to be a valid number; won't parse something like
    `'123.345blah'` into something like `123.456`.
*/
export function parseNum(val) {
  if (a.isNil(val)) return undefined
  if (a.isNum(val)) return a.onlyFin(val)
  return a.finOpt(val)
}

/*
Should be used for parsing keys that may be either numbers, numeric strings, or
non-numeric strings. The output may be nil, number, or string.
*/
export function parseId(val) {
  if (a.isNil(val)) return undefined
  if (a.isFin(val)) return val
  if (a.isStr(val)) return parseNum(val) ?? val
  throw a.errConv(val, `id`)
}

export function jsonEncodeSome(val) {
  return a.isNil(val) ? undefined : JSON.stringify(val)
}

export function maybeStringEncode(val) {
  if (a.isNil(val)) return undefined
  if (a.isScalar(val)) return a.render(val)
  if (a.isDict(val) || a.isArr(val)) return a.jsonEncode(val)
  throw a.errConv(val, `text or JSON`)
}

export function maybeJsonDecode(val) {
  if (!a.isStr(val) || !val) return val

  try {
    return JSON.parse(val)
  }
  catch (err) {
    if (err.name === `SyntaxError`) return val
    throw err
  }
}

export function jsonPretty(val) {return JSON.stringify(val, null, 2)}

export function parseBool(val) {
  return a.isBool(val) ? val : a.boolOpt(val)
}

// TODO remove.
export function textEllipsis(val, len) {return a.ell(val, len)}

export function pluralize(lang, word, count, isAdjective) {
  lang = a.laxStr(lang) || c.LANG_DEFAULT
  a.reqStr(word)
  count = a.laxFin(count)
  const yToIes = word.substr(word.length - 2, word.length)
  if (lang === c.LANG_EN) {
    return count === 1 || isAdjective
      ? word
      : yToIes === `ry` || yToIes === `by`
      ? `${word.substr(0, word.length - 1)}ies`
      : `${word}s`
  }

  return word
}

export function plural(count, part, force, isAdjective)  {
  if (!a.laxFin(count)) return undefined
  const text = pluralize(c.LANG_DEFAULT, part, count, isAdjective)
  if (count === 1 && !force) return text
  return a.str(count, (isAdjective ? `-` : ` `), text)
}

// TODO remove.
export function capitalize(val) {return a.words(val).titleSpaced(val)}

export function strPadLeft(val, pad, len) {
  return a.laxStr(val).padStart(a.laxNat(len), a.laxStr(pad))
}

export function padZero2(val) {return strPadLeft(a.render(val), `0`, 2)}

/*
Restricts the input to something usable with `new Date()` or `Date.UTC()`.
Returns `undefined` for invalid-type inputs.
*/
export function restrictYear(year) {
  if (a.isInt(year)) {
    // This may support years below -100k and above +100k.
    if (a.isInt(Date.UTC(year, 0, 1))) {
      return year
    }
    // Decent safe ceiling. This code is unlikely to remain unchanged long
    // enough to require a larger range.
    return Math.min(year, 9999)
  }
  return undefined
}

/*
Restricts the input day to something usable with `new Date()` or `Date.UTC()`,
between 1 and 31. If valid `year` and `month` are provided, this also restricts
the day to the maximum amount of days in those year and month. For example, for
a February in a non-leap year, this returns no more than 28, but in a leap year,
no more than 29.

Returns `undefined` for invalid-type inputs.
*/
export function restrictDay(year, month, day) {
  if (a.isIntPos(day)) {
    const endOfMonth = new Date(Date.UTC(year, month + 1, 1) - 1)
    const daysInYearMonth = endOfMonth.getUTCDate()

    if (a.isNat(daysInYearMonth)) {
      return Math.min(day, daysInYearMonth)
    }

    return Math.min(day, 31)
  }

  if (day === 0) return 0
  return undefined
}

// Matches the DB type `phone` on the backend,
// which currently allows only digits.
export function trimPhone(val) {
  return a.laxStr(val).replace(/\D/g, ``)
}

export function trimNumber(val) {
  return a.laxStr(val).replace(/[^\d.-]/g, ``)
}

/*
Allows whitespace, digits, hyphens, parentheses, and pluses. Maybe too
permissive. Might want to tighten it up later.
*/
export function stripStringToPhone(val) {
  return a.laxStr(val).replace(/[^\d\s()+-]/g, ``)
}

/*
Uses `Math.random()` instead of `a.uuid` for performance. We don't need
cryptographic entropy here, and the performance difference is huge. Generating
hundreds of UUIDs can be a significant performance cost when rendering or
updating UI. Even this approach is somewhat slow. TODO improve. `Math.random`
is very fast, but string conversion has a measurable cost.
*/
export function weaklyRandomString() {return Math.random().toString().slice(2)}

/*
Somewhat deprecated. Prefer `ctx.seq.nextId()` when possible.
Currently they're equivalent, but we want to change that.
*/
export function rndId() {return `id_${weaklyRandomString()}`}

export function laxRndId(val) {return a.reqValidStr(a.optStr(val) || rndId())}

/*
Similar to `url`, but returns `undefined` if any segment is not a viable key.
TODO rename to `urlOpt`.
*/
export function maybeUrl(...val) {return (val = toValidPath(val)) && url(...val)}

export function siteUrl(...val) {return url(c.SERVER_ORIGIN).addPath(...val)}

export function siteUrlOpt(...val) {return (val = toValidPath(val)) && siteUrl(...val)}

export function maybeSiteUrl(val) {
  if (isUrlLike(val) && url(val).hostname !== ``) return val
  return siteUrlOpt(val)
}

// TODO better name.
function toValidPath(val) {
  val = a.map(val, a.renderLax)
  return val.every(a.truthy) ? val : undefined
}

export function windowUrl(...val) {return url(window.location.origin).addPath(...val)}

export function isSiteUrl(url) {return a.isSubpath(c.SERVER_ORIGIN, url)}

// TODO remove.
export function stripPrefix(val, pre) {return a.stripPre(val, pre)}
export function stripPrefixes(val, pre) {return a.stripPreAll(val, pre)}

// TODO remove.
export function stripSuffix(val, suf) {return a.stripSuf(val, suf)}
export function stripSuffixes(val, suf) {return a.stripSufAll(val, suf)}

// TODO remove.
export function ensurePrefix(val, pre) {return a.optPre(val, pre)}
export function ensureSuffix(val, suf) {return a.optSuf(val, suf)}

export function stripLeadingSlash(val) {return stripPrefix(val, `/`)}
export function stripLeadingSlashes(val) {return stripPrefixes(val, `/`)}
export function stripTrailingSlash(val) {return stripSuffix(val, `/`)}
export function stripTrailingSlashes(val) {return stripSuffixes(val, `/`)}
export function ensureLeadingSlash(val) {return ensurePrefix(val, `/`)}
export function ensureTrailingSlash(val) {return ensureSuffix(val, `/`)}

/*
If the source string both starts and ends with the given prefix-suffix, strips
both the prefix and the suffix from it. Otherwise returns the source string
unchanged.

Unused, TODO remove.
*/
export function stripWrap(src, str) {
  src = a.laxStr(src)
  str = a.laxStr(str)

  if (
    str &&
    (src.length >= str.length * 2) &&
    src.startsWith(str) &&
    src.endsWith(str)
  ) {
    return src.slice(str.length, src.length - str.length)
  }
  return src
}

/*
Must be used for EVERY string included into `<script></script>` tags. The most
common use case is when serializing data as JSON.

When script content contains an unescaped `</script>`, even inside a string or
regexp literal, browsers will interpret it as a closing tag and break the
script. This function escapes it to prevent this.

Assumes that every occurrence of `</script>` is inside a string or regexp
literal.
*/
export function escapeScriptContent(val) {
  return a.laxStr(val).replace(/<\/script>/g, `<\\/script>`)
}

export function vacateString(val) {return a.vac(a.laxStr(val))}

// Removes blank lines and indents.
export function trimLines(string) {
  return a.laxStr(string).replace(/^\s+/gm, ``)
}

export function trimDoubleQuotes(string) {
  return a.laxStr(string).replace(/^"|"$/gm, ``)
}

export function sign(val) {
  a.reqNum(val)
  if (val < 0) return ``
  return `+`
}

/*
This should be the "canonical" test used by client-side text search.

Note: this currently doesn't use regexps because our users are not exactly
familiar with regexps, and escaping a regexp is no better than performing a
substring search. The function's interface (returning a function) allows to
convert this to a regexp-based implementation without changing the calling code.
*/
export function searchTextToTest(searchText) {
  searchText = a.laxStr(searchText).toLowerCase()
  return function testBySearchText(val) {return searchTest(val, searchText)}
}

export function searchTest(src, search) {
  return strNorm(src).includes(strNorm(search))
}

function strNorm(val) {return a.trim(val).toLowerCase()}

export function filterByTitleSearch(src, search) {
  src = a.arr(src)
  search = a.laxStr(search)
  if (!search) return src

  const test = searchTextToTest(search)
  return a.filter(src, val => test(val.title))
}

/*
All page meta titles (`<title></title>`) must use this function.

TODO: add missing meta titles to all pages.

TODO: consider implementing hierarchical titles for sub-pages of large sections
such as personal account.
*/
export function pageTitle(title) {
  title = a.laxStr(title)
  return title || c.BRAND_NAME
}

export function wordsSpaced(val) {return a.words(val).spaced()}

export function urlHasDomain(url, domain) {
  a.reqInst(url, a.Url)
  a.reqStr(domain)
  return url.hostname === domain || url.hostname.endsWith(`.` + domain)
}

/*
Note: we use `www.` because YT considers this the "canonical" form.
Non-`www.` is redirected via 301, wasting network latency.
*/
export function youtubeEmbedUrl(id) {
  id = a.renderLax(id)
  return a.vac(id) && url(`https://www.youtube.com/embed`, id)
}

// "loop" removes related videos from "end screen".
export function vimeoEmbedUrl({id, controls, muted}) {
  id = a.renderLax(id)
  controls = a.laxBool(controls) ?? true
  muted = a.laxBool(muted)

  if (!id) return undefined

  return url(`https://player.vimeo.com/video`, id).setQuery({
    loop: 1,
    controls: um.boolInt(controls),
    muted: um.boolInt(muted),
  })
}

export function youtubeUrlToEmbedUrl(url) {
  a.reqInst(url, a.Url)

  if (urlHasDomain(url, `youtu.be`)) {
    url.query = {start: url.query.get(`t`)}, url
    return youtubeEmbedUrl(url.rel())
  }

  if (!urlHasDomain(url, `youtube.com`)) return undefined
  if (a.isSubpath(`/embed`, url.pathname)) return url
  return youtubeEmbedUrl(url.query.get(`v`))
}

/*
The image extension list was taken from MDN.
Reference: https://developer.mozilla.org/en-us/docs/web/media/formats/image_types
*/
const IMAGE_REGEXP = /[.](?:apng|avif|gif|jpg|jpeg|jfif|pjpeg|pjp|png|svg|webp)$/i

export function isImageUrl(url) {
  a.reqInst(url, a.Url)
  return IMAGE_REGEXP.test(url.pathname)
}

export function onlyImageUrl(url) {return a.only(url, isImageUrl)}

export function giphyUrlToEmbedUrl(src) {
  a.reqInst(src, a.Url)
  if (!urlHasDomain(src, `giphy.com`)) return undefined
  if (isImageUrl(src)) return src

  const pk = src.pathname.match(/[-](\w+)$/)?.[1]
  return a.vac(pk) && url(`https://media.giphy.com/media`, pk, `giphy.gif`)
}

export function vimeoUrlToEmbedUrl(url) {
  a.reqInst(url, a.Url)
  if (!urlHasDomain(url, `vimeo.com`)) return undefined
  if (a.isSubpath(`/video`, url.pathname)) return url
  return undefined
}

// Somewhat duplicated from `PriceWithCurrency`, but generates only a string
// rather than markup. This is stricter: expects only a currency code string,
// not a structure.
export function formatCurrencyCode(curCode) {
  curCode = a.laxStr(curCode)
  if (!curCode) return ``

  return a.str(
    c.CURRENCIES[curCode.toLowerCase()]?.symbol,
    curCode.toUpperCase()
  )
}

export function priceLabel(curCode) {
  const currency = formatCurrencyCode(curCode)
  return currency ? `Price in ${currency}` : `Price`
}

export function maxLengthSubtitle(length) {
  if (!length) return undefined
  return `Maximum ${a.laxNat(length)} characters`
}

// `#hash` -> `hash`
export function hashToId(val) {
  return stripPrefix(val, `#`)
}

// `hash` -> `#hash`
export function idToHash(val) {
  val = a.laxStr(val)
  return val && ensurePrefix(val, `#`)
}

// Counts Unicode characters rather than UTF-16 units.
export function strCharCount(val) {
  let out = 0
  for (const __ of a.laxStr(val)) out++
  return out
}

/*
Returns a string like `January 1970 — February 1980` for the given dates. If
both dates represent the same month, returns only one part, something like
`January 1970`.

Currently always formats in local time, could be incorrect when working in UTC
mode or some other timezone.

TODO move to `util-time.mjs`.
*/
export function dateRangeTitle(start, end) {
  a.reqValidDate(start)
  a.reqValidDate(end)
  const startStr = formatDateHumanNoDay(start)
  const endStr = formatDateHumanNoDay(end)
  return startStr === endStr ? startStr : `${startStr} — ${endStr}`
}

export function isUrlLike(val) {
  return a.isScalar(val) || (a.isObj(val) && `href` in val)
}
export function optUrlLike(val) {return a.opt(val, isUrlLike)}
export function reqUrlLike(val) {return a.req(val, isUrlLike)}

export function url(val, ...vals) {return new Url(val).addPath(...vals)}
export function toUrl(val) {return a.toInst(val, Url)}
export function toUrlOpt(val) {return a.toInstOpt(val, Url)}

export class Url extends a.Url {
  get Query() {return Query}

  /*
  Examples:

    ''         -> []
    '/'        -> []
    '/one/two' -> ['one', 'two']
  */
  pathSplit() {return a.compact(this.pathname.split(`/`))}

  /*
  Examples:

    ''         -> '/'
    '/one/two' -> '/one'
  */
  pathHead() {return `/` + a.laxStr(a.head(this.pathSplit()))}

  queryPatch(val) {return this.query.mut(val), this}
  queryPick(...keys) {return this.query.pick(...keys), this}
  querySetAll(key, val) {return this.query.setAll(key, val), this}
  queryDel(key) {return this.query.delete(key), this}
  withQueryPatch(val) {return this.clone().queryPatch(val)}

  preview(len) {
    len = a.optNat(len) || c.LINK_ELLIPSIS_LENGTH
    return a.inter(this.host, `/`, a.ell(this.rel(), len))
  }

  simple() {return a.reqStr(this.protocol) + a.reqStr(this.hostPath())}

  // TODO rename to `setX`.
  mainCats(...val) {return this.mainCategorySlugs(um.getSlugs(val))}
  concernCats(...val) {return this.concernCategorySlugs(um.getSlugs(val))}
  mainCategorySlugs(...val) {return this.querySetAll(`mainCategorySlugs`, a.flat(val))}
  concernCategorySlugs(...val) {return this.querySetAll(`concernCategorySlugs`, a.flat(val))}
  searchText(val) {return this.querySet(c.SEARCH_TEXT_KEY, val)}

  /*
  We should always provide a specific width, for multiple reasons:

    * Our image resizer doesn't support conversion/optimization
      without specific dimensions.

    * The original file may be arbitrarily large, both in terms of byte count
      and in terms of pixels. Too much pixel density would be wasted.
  */
  imageWidth(val) {
    val = a.laxNat(val) || a.laxNat(c.IMAGE_WIDTH_LARGE)
    return this.querySet(c.IMAGE_SERVER_QUERY_KEY_WIDTH, val)
  }
}

export function queryToDictAll(src) {return a.toInst(src, Query).toDictAll()}

export class Query extends a.Query {
  pick(...keys) {
    for (const key of this.keys()) if (!keys.includes(key)) this.delete(key)
    return this
  }

  // TODO consider moving to library superclass.
  pop(key) {try {return this.get(key)} finally {this.delete(key)}}

  // TODO consider moving to library superclass.
  popAll(key) {try {return this.getAll(key)} finally {this.delete(key)}}
}

// Not exactly a "text" function.
// Placed here because `Url` and `Query` are here.
export function queryPop(url, key) {
  a.reqInst(url, a.Url)
  a.reqValidStr(key)
  try {return url.query.get(key)}
  finally {url.query.delete(key)}
}

/*
Note: the generated URL must be interpolated into HTML, and `{}` characters must
be raw, not URL-encoded, otherwise Google will fail to use it. By default, both
`window.URL` and our `Url` would encode these characters.
*/
export function googleSearchTemplateUrl() {
  return (
    url(c.SERVER_ORIGIN).addPath(`search`, `services`).simple() +
    `?searchText={search_term_string}`
  )
}

/*
Note: we can't use `URL` for constructing mobile URLs, because browsers have
stupid special-case whitelists of allowed schemes, and don't allow
`corespirit://` or `intent://`.
*/
export function mobileUrl({platform, ...params}) {
  if (platform === c.PLATFORM_ANDROID) return mobileUrlAndroid(params)
  if (platform === c.PLATFORM_IOS) return mobileUrlIos(params)
  throw errInvalidPlatform(platform)
}

function errInvalidPlatform(val) {
  return Error(`unknown platform ${a.show(val)}`)
}

// See the comment on `mobileUrl`.
export function mobileUrlAndroid({
  pathname = `callback`,
  package: pkg = c.ANDROID_PACKAGE_NAME,
  scheme = c.MOBILE_URL_SCHEME,
  ...rest
}) {
  return a.assign(
    url()
      .setProtocol(`intent://`)
      .setPathname(pathname)
      .setHash(androidIntentHash({package: pkg, scheme})),
    rest,
  )
}

// See the comment on `mobileUrl`.
export function mobileUrlIos({scheme = c.MOBILE_URL_SCHEME, ...rest}) {
  return a.assign(url().setProtocol(schemeToProtocol(scheme)), rest)
}

function schemeToProtocol(val) {return a.optSuf(a.optSuf(val, `:`), `//`)}

/*
Examples:

  {package: 'one'}                -> `Intent;package=one;end`
  {package: 'one', scheme: 'two'} -> `Intent;package=one;scheme=two;end`

Keys are sorted alphabetically.
*/
function androidIntentHash(vals) {
  return [
    `Intent`,
    ...a.mapCompact(a.entries(vals), encodeAndroidIntentPair),
    `end`,
  ].join(`;`)
}

function encodeAndroidIntentPair([key, val]) {
  if (a.isNil(val)) return ``

  a.reqStr(key)
  a.reqStr(val)

  if (val.includes(`;`)) throw Error(`unexpected ";" in Android intent "${key}": "${val}"`)
  return `${key}=${val}`
}

/*
When using the same regexp instance for multiple unrelated operations, we MUST
reset `.lastIndex` before each use. On regexps with a global flag, this
property is set and read by just about every operation, including `.test`.
Without resetting it, the index from the previous operations would affect other
operations, leading to incorrect behaviors. Normally this gotcha goes
unnoticed, because most of the time, regexps are instantiated inline.
*/
export function regRes(reg) {
  a.reqReg(reg)
  reg.lastIndex = 0
  return reg
}

/*
export function strMatch(str, reg) {return a.laxStr(str).match(regRes(reg))}
*/

// Converts strings like `one.two.three` into paths like `['one', 'two', 'three']`.
// TODO better name.
export function strPath(val) {return a.split(val, `.`)}

// TODO remove.
export function strTrim(val) {return a.trim(val)}

/*
Represents an entry in HTTP header `Accept-Language` or `navigator.languages`.

The entry may contain an optional locale: "en" or "en-US".

The entry may contain an optional q-factor: "en-US;q=0.9".
*/
export class ClientLang extends a.Emp {
  constructor(src) {super().mut(src)}

  mut(src) {
    if (a.isNil(src)) return this
    if (a.isStr(src)) return this.mutFromStr(src)
    if (a.isStruct(src)) return this.mutFromStruct(src)
    throw a.errConvInst(src, this)
  }

  mutFromStr(src) {
    const mat = a.trim(src).match(regRes(this.constructor.re))
    if (mat) {
      this.lang = a.reqStr(mat[1])
      this.weight = a.finOpt(mat[2])
    }
    return this
  }

  mutFromStruct(src) {
    a.reqStruct(src)
    this.lang = a.reqStr(src.lang)
    this.weight = a.optFin(src.weight)
    return this
  }

  isValid() {return !!this.lang && this.lang !== `*`}

  // Inefficient, could be improved.
  code() {return this.lang?.split(/[_-]/g)[0] || ``}

  toString() {
    const code = this.code()
    for (const val of c.LANGUAGES) if (val.code === code) return val.title
    return code
  }

  /*
  Doesn't have a closing `$` because real-world inputs may contain
  additional stuff we're not ready to parse. See the test.
  Expects input to be trimmed.
  */
  static re = /^(\*|[\w-]*)(?:\s*;\s*q=(\d+(?:[.]\d+)?))?/
}

export function mapSplitTrim(val, delim) {return a.mapCompact(a.laxStr(val).split(delim), a.trim)}

/*
Parsed representation of HTTP header `Accept-Language` or `navigator.languages`.
See `test_ClientLangs` for examples.
*/
export class ClientLangs extends a.ClsVec {
  get cls() {return ClientLang}

  parse(val) {
    return this.addFrom(mapSplitTrim(val, `,`))
  }

  add(val) {
    val = this.make(val)
    if (val?.isValid()) super.add(val)
    return this
  }

  sorted() {return this.$.sort(clientLangCompare), this}
  viable() {return a.procure(this.$, viableClientLang) || c.LANG_DEFAULT}
}

function clientLangCompare(one, two) {
  a.reqInst(one, ClientLang)
  a.reqInst(two, ClientLang)

  if (!one.weight && two.weight) return -1
  if (one.weight && !two.weight) return 1
  if (one.weight > two.weight) return -1
  if (one.weight < two.weight) return 1
  return 0
}

function viableClientLang(val) {
  val = a.reqInst(val, ClientLang).code()
  return c.LANGS.includes(val) ? val : undefined
}

/*
TODO consider stricter validation. For example, we could verify that the input
contains exactly one character which is printable.
*/
export function isEmojiChar(val) {return a.isStr(val) && !a.isBlank(val)}
export function reqEmojiChar(val) {return a.req(val, isEmojiChar)}
export function reqEmojiCharArr(val) {return a.reqArrOf(val, isEmojiChar)}

export class EmojiCharSet extends ucc.Bset {
  req(val) {return reqEmojiChar(val)}
  mut(src) {return this.clear(), super.mut(src)}
}

export function ellMid(src) {return a.ell(src, c.PREVIEW_LENGTH_MID)}

export function joinLines(...val) {return a.joinLinesOptLax(val)}
export function joinParagraphs(...val) {return a.joinOptLax(val, `\n\n`)}
export function joinKebab(...val) {return a.joinOptLax(val, `-`)}
export function joinSnake(...val) {return a.joinOptLax(val, `_`)}
export function joinSlashed(...val) {return a.joinOptLax(val, `/`)}
export function joinComma(val) {return a.joinOptLax(val, `, `)}
export function joinSemi(...val) {return a.joinOptLax(val, `; `)}
export function joinColon(...val) {return a.joinOptLax(val, `:`)}

export function emailLink(val) {
  return a.vac(a.optPre(a.renderLax(val), `mailto:`))
}

export function phoneLink(val) {
  return a.vac(a.optPre(phoneToInternational(val), `tel:`))
}

export function phoneToInternational(val) {
  return a.optPre(a.renderLax(val), `+`)
}

/*
Example: `file.ext` → `ext`.
Difference from `p.posix.ext`: no leading dot.
TODO make compatible with Windows paths.
TODO better name.
TODO move to `@mitranim/js/path.mjs`.
*/
export function extClean(val) {
  return a.stripPre(p.posix.ext(val), p.posix.extSep)
}

// [`one`, `two`] → `.one, .two`
export function extsToFileInputAccept(val) {return joinComma(a.map(val, predot))}

function predot(val) {return a.optPre(val, `.`)}

export function textEq(one, two) {return a.laxStr(one) === a.laxStr(two)}

export function parenthesize(val) {return optWrap(val, `(`, `)`)}

// TODO move to `@mitranim/js`.
export function optWrap(src, pre, suf) {
  src = a.laxStr(src)
  a.reqStr(pre)
  a.reqStr(suf)

  return src && (
    ``
    + (src.startsWith(pre) ? `` : pre)
    + src
    + (src.endsWith(suf) ? `` : suf)
  )
}

export function imageUrlSmallSize(val) {
  return imageUrl(val, c.IMAGE_WIDTH_SMALL)
}

export function imageUrlMediumSize(val) {
  return imageUrl(val, c.IMAGE_WIDTH_MEDIUM)
}

export function imageUrlGiantSize(val) {
  return imageUrl(val, c.IMAGE_WIDTH_GIANT)
}

export function imageUrl(val, width) {
  return imageNameToUrl(um.getName(val), width)
}

export function imageNameToUrl(val, width) {
  if (!a.optStr(val) || !c.IMAGE_SERVER_URL) return undefined
  return url(c.IMAGE_SERVER_URL, val).imageWidth(width)
}

const codePointA = `A`.codePointAt(0)
const codePointZ = `Z`.codePointAt(0)

export function isCodePointAsciiLetterUpper(val) {
  return val >= codePointA && val <= codePointZ
}

export function isRoutePattern(val) {return a.isStr(val) || a.isReg(val)}
export function reqRoutePattern(val) {return a.req(val, isRoutePattern)}

/*
The constraint is intentionally much more relaxed than in `csc.slug` on the
backend, so that we don't need to synchronize them.
*/
export function isValidSlug(val) {return a.isStr(val) && /^[^\s/?#]+$/.test(val)}

export function slugMention(slug) {
  a.optStr(slug)
  if (isValidSlug(slug)) return `@` + slug
  return ``
}
